import { useAuth } from "@/context/authContext";
import { api } from "@/utils/api";
import { useState, type Dispatch, type SetStateAction } from "react";
import { toast } from "sonner";

export const useSignUp = () => {
  const { dispatch } = useAuth();
  const [loading, setLoading] = useState<{
    loadExplorations: boolean;
    signUp: boolean;
    preferences: boolean;
  }>({
    loadExplorations: false,
    signUp: false,
    preferences: false,
  });

  const loadExplorations = async (
    sessionId: string,
    setExplorations: Dispatch<SetStateAction<any[]>>,
  ) => {
    try {
      setLoading((prev) => ({ ...prev, loadExplorations: true }));
      const response = await api(
        `/explorations?shape=student&session=${sessionId}`,
        { method: "GET" },
        dispatch,
      );
      setExplorations(response.explorations);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, loadExplorations: false }));
    }
  };

  const signUp = async (sessionId: string, explorationId: string) => {
    try {
      setLoading((prev) => ({ ...prev, signUp: true }));
      const response = await api(
        `/enrollments/enrollment/${sessionId}/${explorationId}`,
        {
          method: "POST",
        },
        dispatch,
      );
      toast.success(response.message);
      return {
        enrollment: response.enrollment,
        enrollments: response.enrollments,
        session: sessionId,
      };
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, signUp: false }));
    }
  };

  const loadPreferenceContext = async (sessionId: string) => {
    try {
      const response = await api(
        `/enrollments/preferences/me/for-session/${sessionId}`,
        { method: "GET" },
        dispatch,
      );
      return {
        myPreference: response.myPreference,
        lockedChoice1: response.lockedChoice1,
      };
    } catch {
      return { myPreference: null, lockedChoice1: null };
    }
  };

  const preferences = async (sessionId: string, choices: string[]) => {
    try {
      setLoading((prev) => ({ ...prev, preferences: true }));
      const response = await api(
        `/enrollments/preferences/${sessionId}`,
        {
          method: "POST",
          body: {
            choices,
          },
        },
        dispatch,
      );
      toast.success(response.message);
      return { ...response.preferences, session: sessionId };
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, preferences: false }));
    }
  };

  return {
    loadExplorations,
    signUp,
    preferences,
    loadPreferenceContext,
    loading,
  };
};
